import { useState, useEffect } from 'react'
import { AdminSettingsService } from '../services/adminSettings'
import { AuthService } from '../services/auth'
import type { AuthUser } from '../services/auth'
import type { AdminSetting, SettingsGroup, AdminSettingKey } from '../types/adminSettings'

const AdminSettings = () => {
  const [settings, setSettings] = useState<AdminSetting[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [editedValues, setEditedValues] = useState<Record<string, string | number | boolean>>({})
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null)

  // Define settings groups for UI organization
  const settingsGroups: SettingsGroup[] = [
    {
      category: 'general',
      label: '一般設定',
      fields: [
        {
          key: 'site_name',
          label: 'サイト名',
          type: 'text',
          description: 'アプリケーションの表示名'
        },
        {
          key: 'site_description',
          label: 'サイト説明',
          type: 'textarea',
          description: 'サイトの説明文',
          rows: 3
        },
        {
          key: 'maintenance_mode',
          label: 'メンテナンスモード',
          type: 'boolean',
          description: 'メンテナンスモードを有効にする'
        }
      ]
    },
    {
      category: 'video',
      label: '動画設定',
      fields: [
        {
          key: 'ranking_period_days',
          label: 'ランキング集計期間（日）',
          type: 'number',
          description: 'ランキングの集計対象期間（日数）。0で全期間を集計します',
          min: 0,
          max: 365,
          step: 1
        }
      ]
    }
    // Note: Student summary settings are now managed in the "サマリー生成" tab
  ]

  useEffect(() => {
    loadSettings()
    loadCurrentUser()
  }, [])

  const loadCurrentUser = async () => {
    try {
      const user = await AuthService.getCurrentUser()
      setCurrentUser(user)
    } catch (err) {
      console.error('Error loading current user:', err)
    }
  }

  const loadSettings = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const response = await AdminSettingsService.getAllSettings()
      if (response.success) {
        setSettings(response.data)
        // Initialize edited values with current settings
        const initialValues: Record<string, string | number | boolean> = {}
        response.data.forEach(setting => {
          initialValues[setting.key] = setting.value as string | number | boolean
        })
        setEditedValues(initialValues)
      } else {
        setError(response.error || '設定の読み込みに失敗しました')
      }
    } catch (err) {
      setError('設定の読み込み中にエラーが発生しました')
      console.error('Error loading settings:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (key: AdminSettingKey, value: string | number | boolean) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Prepare updates array
      const updates = Object.entries(editedValues)
        .map(([key, value]) => ({ key: key as AdminSettingKey, value }))
        .filter(update => {
          const currentSetting = settings.find(s => s.key === update.key)
          return currentSetting && JSON.stringify(currentSetting.value) !== JSON.stringify(update.value)
        })

      if (updates.length === 0) {
        setSuccessMessage('変更はありません')
        return
      }

      const result = await AdminSettingsService.updateMultipleSettings(updates)
      
      if (result.success) {
        setSuccessMessage(`${updates.length}件の設定を更新しました`)
        await loadSettings() // Reload to get updated timestamps
      } else {
        setError(`更新エラー: ${result.errors.join(', ')}`)
      }
    } catch (err) {
      setError('設定の保存中にエラーが発生しました')
      console.error('Error saving settings:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    // Reset edited values to current settings
    const resetValues: Record<string, string | number | boolean> = {}
    settings.forEach(setting => {
      resetValues[setting.key] = setting.value as string | number | boolean
    })
    setEditedValues(resetValues)
    setSuccessMessage('変更をリセットしました')
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError(null)
    setPasswordSuccess(null)

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('すべてのフィールドを入力してください')
      return
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('新しいパスワードと確認パスワードが一致しません')
      return
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordError('新しいパスワードは8文字以上である必要があります')
      return
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('新しいパスワードは現在のパスワードと異なる必要があります')
      return
    }

    setIsChangingPassword(true)

    try {
      // First verify current password by attempting to sign in
      if (!currentUser?.email) {
        throw new Error('ユーザー情報が取得できません')
      }

      // Attempt to sign in with current credentials to verify current password
      await AuthService.signInWithPassword(currentUser.email, passwordForm.currentPassword)

      // If that succeeds, update the password using Supabase auth
      // We'll create a method in AuthService for this
      await AuthService.updateAdminPassword(passwordForm.newPassword)

      // Log the password change action
      await AuthService.logAdminAction('password_change', 'admin_account', currentUser.id)

      setPasswordSuccess('パスワードが正常に変更されました')
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
    } catch (error: unknown) {
      console.error('Password change error:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      if (errorMessage.includes('Invalid login credentials')) {
        setPasswordError('現在のパスワードが正しくありません')
      } else {
        setPasswordError('パスワードの変更に失敗しました: ' + errorMessage)
      }
    } finally {
      setIsChangingPassword(false)
    }
  }

  const renderField = (field: SettingsGroup['fields'][0]) => {
    const value = editedValues[field.key] ?? ''
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            value={String(value)}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            rows={field.rows || 4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
            placeholder={field.description}
          />
        )
      
      case 'number':
        return (
          <input
            type="number"
            value={Number(value)}
            onChange={(e) => handleInputChange(field.key, parseFloat(e.target.value) || 0)}
            min={field.min}
            max={field.max}
            step={field.step}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )
      
      case 'boolean':
        return (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => handleInputChange(field.key, e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">有効</span>
          </div>
        )
      
      case 'select':
        return (
          <select
            value={String(value)}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )
      
      default:
        return (
          <input
            type="text"
            value={String(value)}
            onChange={(e) => handleInputChange(field.key, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={field.description}
          />
        )
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">システム設定</h2>
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            リセット
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      <div className="space-y-8">
        {/* Password Change Section */}
        {currentUser && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">パスワード変更</h3>
            <div className="mb-4 text-sm text-gray-600">
              現在のユーザー: <span className="font-medium">{currentUser.name}</span> ({currentUser.email})
            </div>
            
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  現在のパスワード
                </label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  新しいパスワード
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  minLength={8}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">8文字以上で入力してください</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  新しいパスワード（確認）
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{passwordError}</p>
                </div>
              )}

              {passwordSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm">{passwordSuccess}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isChangingPassword}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isChangingPassword ? 'パスワード変更中...' : 'パスワードを変更'}
              </button>
            </form>
          </div>
        )}

        {/* System Settings */}
        {settingsGroups.map(group => (
          <div key={group.category} className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{group.label}</h3>
            <div className="space-y-6">
              {group.fields.map(field => (
                <div key={field.key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {field.label}
                  </label>
                  {renderField(field)}
                  {field.description && (
                    <p className="text-sm text-gray-500">{field.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default AdminSettings